/**
 * MeterBar — a filled track with an optional target marker.
 *
 * Two jobs in this report: the plan-vs-fact bar for client enquiries (fill =
 * what you got, marker = what the budget should have bought) and the per-
 * component bars in the score breakdown.
 *
 * No Progress primitive in the design system (D5).
 *
 * @format
 */

import {Box} from '@mui/material';
import {styled} from '@mui/material/styles';

import {spacingPx} from 'ui/components/layout/spacing';
import {text} from 'ui/theme/typography';
import {surfaceVar} from 'ui/theme/surfaceContext';
import {RadiusPill} from 'ui/tokens/ts/radii';

import type {PaletteTone} from '../content';

export interface MeterBarProps {
  /** 0..1. Values outside the range are clamped. */
  value: number;
  tone?: PaletteTone | 'primary';
  /** Track height in px. `s` bars sit inside tables, `l` is the hero bar. */
  height?: number;
  /** Text rendered inside the fill (hero bar only — needs height ≥ 28). */
  fillLabel?: string;
  /** Marker position 0..1, e.g. the plan when the fill is the fact. */
  marker?: number;
  markerLabel?: string;
  className?: string;
}

const Track = styled(Box, {
  shouldForwardProp: prop => !String(prop).startsWith('$'),
})<{$height: number}>(({$height}) => ({
  position: 'relative',
  height: $height,
  width: '100%',
  minWidth: 60,
  backgroundColor: surfaceVar('surface3'),
  borderRadius: RadiusPill,
  overflow: 'hidden',
}));

const Fill = styled(Box, {
  shouldForwardProp: prop => !String(prop).startsWith('$'),
})<{$tone: PaletteTone | 'primary'; $value: number; $labelled: boolean}>(({
  theme,
  $tone,
  $value,
  $labelled,
}) => ({
  height: '100%',
  width: `${$value * 100}%`,
  backgroundColor: theme.palette[$tone].main,
  borderRadius: RadiusPill,
  display: 'flex',
  alignItems: 'center',
  // Отступ под подпись — только когда подпись есть. Иначе он остаётся
  // шириной у заливки нулевой длины: ноль баллов рисовался бы цветным
  // огрызком в 16px, то есть «немного» вместо «нисколько».
  paddingLeft: $labelled ? spacingPx('m') : 0,
  ...text('label.l'),
  color: theme.palette[$tone].contrastText,
  whiteSpace: 'nowrap',
  transition: 'width 400ms ease',
}));

const Marker = styled(Box, {
  shouldForwardProp: prop => !String(prop).startsWith('$'),
})<{$at: number}>(({$at}) => ({
  position: 'absolute',
  top: 0,
  bottom: 0,
  left: `${$at * 100}%`,
  width: 2,
  backgroundColor: surfaceVar('onSurfaceLight'),
}));

const MarkerLabel = styled(Box)({
  ...text('label.l'),
  position: 'absolute',
  right: spacingPx('m'),
  top: '50%',
  transform: 'translateY(-50%)',
  color: surfaceVar('onSurfaceLight'),
  whiteSpace: 'nowrap',
});

export function MeterBar({
  value,
  tone = 'primary',
  height = 8,
  fillLabel,
  marker,
  markerLabel,
  className,
}: MeterBarProps) {
  const clamped = Math.max(0, Math.min(1, value));
  const label = height >= 28 && fillLabel ? fillLabel : null;
  return (
    <Track
      $height={height}
      className={className}
      role='meter'
      aria-valuenow={Math.round(clamped * 100)}
      aria-valuemin={0}
      aria-valuemax={100}
    >
      <Fill $tone={tone} $value={clamped} $labelled={label !== null}>
        {label}
      </Fill>
      {marker !== undefined && <Marker $at={Math.max(0, Math.min(1, marker))} />}
      {markerLabel && <MarkerLabel>{markerLabel}</MarkerLabel>}
    </Track>
  );
}
